/**
 * Home view greeting strip per design README section 5.1 and spec
 * section 15.10. Left: date eyebrow + H1 greeting + commit meta.
 * Right: streak block (a simple committed-progress count for M10;
 * the full streak counter lands with the M11 / M13 streak rollup).
 */

import { useMemo } from 'react';

interface GreetingProps {
  greeting: string;
  /** YYYY-MM-DD from the morning brief endpoint. */
  todayIso: string;
  /** First name to address. */
  userName?: string;
  /** Today's commit target. */
  commitTarget: number;
  committed: number;
  done: number;
}

export function Greeting({
  greeting,
  todayIso,
  userName,
  commitTarget,
  committed,
  done,
}: GreetingProps) {
  const eyebrow = useMemo(() => formatDateEyebrow(todayIso), [todayIso]);
  const meta = formatCommitMeta(commitTarget, committed, done);

  return (
    <div className="flex items-start justify-between gap-mx-4 pb-mx-5 pt-mx-3">
      <div className="flex flex-col gap-mx-3">
        {/* Round 4 PR #42: the date eyebrow was crammed at the top of
            the home view in a 10.5px mono-label that barely read. Bumped
            to body-sm with letter-spacing and font-medium so the date
            anchors the page properly without competing with the H1. */}
        <p
          className="text-mx-body-sm font-medium uppercase tracking-[0.18em] text-mx-grey-300"
          data-testid="home-eyebrow"
        >
          {eyebrow}
        </p>
        <h1 className="font-display text-mx-h1 font-semibold text-mx-white">
          {greeting}
          {userName ? `, ${userName}.` : '.'}
        </h1>
        <p className="text-mx-body-sm text-mx-grey-mid" data-testid="home-commit-meta">
          {meta}
        </p>
      </div>
    </div>
  );
}

/**
 * Format `2026-05-28` as `WEDNESDAY 28 MAY · WEEK 22`.
 */
function formatDateEyebrow(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const weekday = d.toLocaleDateString('en-AU', { weekday: 'long' }).toUpperCase();
  const day = d.getDate();
  const month = d.toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
  const week = isoWeek(d);
  return `${weekday} ${day} ${month} · WEEK ${week}`;
}

/**
 * ISO 8601 week number, used in the date eyebrow.
 */
function isoWeek(d: Date): number {
  const target = new Date(d.valueOf());
  const dayNumber = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target.getTime() - firstThursday.getTime();
  return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Commit meta sentence per design README section 5.1.
 *
 * Examples:
 *   `You committed to 3 sales actions today. 0 done, 3 to go.`
 *   `You haven't committed today. Pick 3 to commit.`
 *   `Inbox zero. No sales actions outstanding.`
 */
function formatCommitMeta(target: number, committed: number, done: number): string {
  if (committed === 0 && done === 0)
    return `You haven't committed today. Pick ${target} to commit.`;
  if (committed === 0) return `${done} sales actions done today. No commits yet.`;
  const remaining = Math.max(0, committed - done);
  return `You committed to ${committed} sales actions today. ${done} done, ${remaining} to go.`;
}
